import type { App } from 'vue'
import type { AxiosResponse } from 'axios'
import axios from 'axios'
import VueAxios from 'vue-axios'
import router from '@/router'

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description property to share vue instance
   */
  public static vueInstance: App

  /**
   * @description initialize vue axios
   */
  public static init(app: App<Element>) {
    ApiService.vueInstance = app
    ApiService.vueInstance.use(VueAxios, axios)
    ApiService.vueInstance.axios.defaults.baseURL = import.meta.env.VITE_APP_API_URL
    ApiService.vueInstance.axios.defaults.withCredentials = true
    ApiService.vueInstance.axios.defaults.withXSRFToken = true
    ApiService.vueInstance.axios.defaults.headers.common['Content-Type'] = 'application/json'
    ApiService.vueInstance.axios.interceptors.response.use(
      (response) => {
        // If the response is successful, just return it
        return response
      },
      (error) => {
        // If the response has a status of 401 (Unauthorized), redirect to the login page
        if (error.response && error.response.status === 401) {
          ApiService.vueInstance.axios.defaults.headers.common['Cookie'] = ''
          router.push({ name: 'sign-in' })
          // Return a resolved promise to prevent the error from propagating
          return Promise.resolve({ data: null })
        }

        // If the response has a different error status, reject the promise
        return Promise.reject(error)
      }
    )
  }

  /**
   * @description send the GET HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param params
   */
  public static query(resource: string, params: any): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.get(resource, params)
  }

  /**
   * @description send the GET HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param slug
   */
  public static get(resource: string, slug = '' as string): Promise<AxiosResponse> {
    const url = slug.length > 0 ? `${resource}/${slug}` : resource
    return ApiService.vueInstance.axios.get(url)
  }

  /**
   * @description set the POST HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param params
   */
  public static post(resource: string, params: any): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.post(`${resource}`, params)
  }

  /**
   * @description send the UPDATE HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param slug
   * @param params
   */
  public static update(resource: string, slug: string, params: any): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}/${slug}`, params)
  }

  /**
   * @description Send the PUT HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param params
   */
  public static put(resource: string, params: any): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.put(`${resource}`, params)
  }

  /**
   * @description Send the PATCH HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   * @param params
   */
  public static patch(resource: string, params: any): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.patch(`${resource}`, params)
  }

  /**
   * @description Send the DELETE HTTP request
   * @returns Promise<AxiosResponse>
   * @param resource
   */
  public static delete(resource: string): Promise<AxiosResponse> {
    return ApiService.vueInstance.axios.delete(resource)
  }
}

export default ApiService
